/* eslint-disable react/prop-types */
/**
 * AttachmentsScopeButton — jumps to the deal's Attachments tab already scoped
 * to one child record (or to one parent TYPE, when no id is passed).
 *
 * Why it exists: attaching a file below the deal level was always supported by
 * the API and by AttachmentsTab's scope picker, but the only route to it was
 * Attachments tab -> Parent type radio -> Entity select. UAT testers never
 * found it and reported attachments as "Deal only" / "no Add option at the
 * Lease level". A paperclip on the record itself is the discoverable route.
 *
 * Implementation: the deal-detail tabs are URL-driven (`?tab=`), so this only
 * has to rewrite the query string — AttachmentsTab reads `parent_table` /
 * `parent_id` on mount and presets its scope from them.
 */
import { Button, Tooltip } from "antd";
import { PaperClipOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router";

const AttachmentsScopeButton = ({
  parentTable,
  parentId,
  label = "Attachments",
  tooltip,
  iconOnly = false,
  ...buttonProps
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const goToAttachments = () => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "attachments");
    next.set("parent_table", parentTable);
    if (parentId == null) next.delete("parent_id");
    else next.set("parent_id", String(parentId));
    setSearchParams(next);
  };

  const button = (
    <Button
      size="small"
      type={iconOnly ? "text" : "default"}
      icon={<PaperClipOutlined />}
      onClick={goToAttachments}
      aria-label={tooltip || label}
      {...buttonProps}
    >
      {iconOnly ? null : label}
    </Button>
  );

  return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
};

export default AttachmentsScopeButton;
